import { useCallback, useMemo, useReducer } from "react";

export interface IUseWizardOptions<TStepKeys extends string> {
    startAtStep?: TStepKeys;
    stepInfo?: Record<
        TStepKeys,
        {
            reset?: () => void;
        }
    >;
}
interface IStepReducerAction<TStepKeys extends string> {
    action?: "next" | "previous" | "reset";
    step?: TStepKeys;
}

export default function useWizard<TStepKeys extends string>(
    steps: TStepKeys[],
    props?: IUseWizardOptions<TStepKeys>
) {
    //wizard
    const [step, dispatchStep] = useReducer(
        stepReducer,
        getInicialStepState(props?.startAtStep)
    );

    function stepReducer(
        state: { step: TStepKeys; visiteds: Array<TStepKeys> },
        { action, step }: IStepReducerAction<TStepKeys>
    ) {
        let stepToGo = state.step;
        let newVisiteds = state.visiteds;

        if (action === "reset") {
            const indexEtapa = steps.findIndex((etapa) => etapa === step);
            for (let index = indexEtapa + 1; index < steps.length; index++) {
                const etapa = steps[index];
                props?.stepInfo?.[etapa]?.reset?.();
            }
            newVisiteds = state.visiteds.slice(0, indexEtapa + 1);
        } else {
            if (action === "next" || action === "previous") {
                const currentIndex = steps.indexOf(state.step);
                if (
                    currentIndex > -1 &&
                    action === "next" &&
                    currentIndex < steps.length - 1
                )
                    stepToGo = steps[currentIndex + 1];
                else if (
                    currentIndex > -1 &&
                    action === "previous" &&
                    currentIndex > 0
                )
                    stepToGo = steps[currentIndex - 1];
            } else if (step) stepToGo = step;

            if (!state.visiteds.find((s) => s === stepToGo))
                newVisiteds.push(stepToGo);
        }

        return {
            step: stepToGo,
            visiteds: newVisiteds,
        };
    }
    function getInicialStepState(inicial?: TStepKeys) {
        const inicialState = new Array<TStepKeys>();
        if (inicial) {
            const index = steps.findIndex((e) => e === inicial);
            steps.slice(0, index + 1).forEach((e) => inicialState.push(e));
        } else inicialState.push(steps[0]);

        return {
            step: inicial ?? steps[0],
            visiteds: inicialState,
        };
    }

    const goTo = useCallback((step: TStepKeys) => {
        dispatchStep({
            step,
        });
    }, []);
    const next = useCallback(() => {
        dispatchStep({
            action: "next",
        });
    }, []);
    const previous = useCallback(() => {
        dispatchStep({
            action: "previous",
        });
    }, []);
    const changeStep = useMemo(
        () => ({
            goTo,
            next,
            previous,
        }),
        [goTo, next, previous]
    );
    const resetStep = useCallback((step: TStepKeys) => {
        dispatchStep({
            action: "reset",
            step,
        });
    }, []);

    return {
        ...step,
        changeStep,
        goTo,
        resetStep,
        steps,
    };
}
